class ArkInfluencerDiscountController {
  constructor() {
    this.config = {
      enabled: true,
      queryParameter: 'ark_flow',
      queryValue: 'influencer',
      cookieName: 'ark_influencer',
      cookieDays: 30,
      attributeKey: '_discount_track',
      attributeValue: 'influencer',
      expirationAttributeKey: '_discount_track_expires',
      tiers: [
        {
          threshold: 5000,
          amount: 2500,
          code: "ARK25"
        },
        {
          threshold: 8000,
          amount: 3500,
          code: "ARK35"
        },
        {
          threshold: 12000,
          amount: 4500,
          code: "ARK45"
        }
      ],
      normal: {
        enabled: true,
        tiers: [
          {
            quantity: 2,
            code: "ARK2"
          },
          {
            quantity: 3,
            code: "ARK3"
          },
          {
            quantity: 4,
            code: "ARK4"
          }
        ]
      }
    };
    this.syncQueue = Promise.resolve();
    this.pendingSyncs = 0;
    this.initialized = false;
    this.initialCartAttributeActive = document.documentElement.dataset.influencerDiscountActive === 'true';
    this.initialCartAttributePresent = document.documentElement.dataset.influencerDiscountPresent === 'true';
    this.initialCartExpiration = this.normalizeExpiration(document.documentElement.dataset.influencerDiscountExpires);
    this.initialManagedCodePresent = document.documentElement.dataset.influencerManagedDiscountActive === 'true';
    this.initialNormalManagedCodePresent = document.documentElement.dataset.normalManagedDiscountActive === 'true';
    this.initialCartItemCount = Number(document.documentElement.dataset.cartItemCount || 0);

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
    } else {
      this.init();
    }
  }

  normalize(value) {
    return String(value || '').trim().toLowerCase();
  }

  getCookie(name) {
    const prefix = encodeURIComponent(name) + '=';
    const item = document.cookie.split('; ').find((cookie) => cookie.indexOf(prefix) === 0);
    return item ? decodeURIComponent(item.substring(prefix.length)) : '';
  }

  currentTimestamp() {
    return Math.floor(Date.now() / 1000);
  }

  normalizeExpiration(value) {
    const timestamp = Math.floor(Number(value));
    return Number.isFinite(timestamp) && timestamp > 0 ? timestamp : 0;
  }

  createExpirationTimestamp() {
    const days = Math.max(1, Number(this.config.cookieDays) || 30);
    return this.currentTimestamp() + (days * 86400);
  }

  cookieExpiration() {
    return this.normalizeExpiration(this.getCookie(this.config.cookieName));
  }

  setActivationCookie(expiresAt) {
    const expiration = this.normalizeExpiration(expiresAt);
    const maxAge = expiration - this.currentTimestamp();
    if (maxAge <= 0) {
      this.removeActivationCookie();
      return;
    }

    let cookie = encodeURIComponent(this.config.cookieName) + '=' + expiration + '; max-age=' + maxAge + '; path=/; SameSite=Lax';
    if (location.protocol === 'https:') cookie += '; Secure';
    document.cookie = cookie;
  }

  removeActivationCookie() {
    document.cookie = encodeURIComponent(this.config.cookieName) + '=; max-age=0; path=/; SameSite=Lax';
  }

  activationInUrl() {
    const url = new URL(location.href);
    const explicitActivation = this.config.queryParameter &&
      this.normalize(url.searchParams.get(this.config.queryParameter)) === this.normalize(this.config.queryValue);

    if (explicitActivation) {
      url.searchParams.delete(this.config.queryParameter);
      history.replaceState({}, document.title, url.toString());
    }

    return explicitActivation;
  }

  cookieIsActive() {
    return this.cookieExpiration() > this.currentTimestamp();
  }

  cartAttributeExpiration(cart) {
    if (!cart || !cart.attributes) return 0;
    return this.normalizeExpiration(cart.attributes[this.config.expirationAttributeKey]);
  }

  cartAttributeIsPresent(cart) {
    if (!cart || !cart.attributes) return false;
    return Boolean(
      cart.attributes[this.config.attributeKey] ||
      cart.attributes[this.config.expirationAttributeKey]
    );
  }

  cartAttributeIsActive(cart) {
    return cart && cart.attributes &&
      this.normalize(cart.attributes[this.config.attributeKey]) === this.normalize(this.config.attributeValue) &&
      this.cartAttributeExpiration(cart) > this.currentTimestamp();
  }

  activeExpiration(cart) {
    const validExpirations = [
      this.cookieExpiration(),
      cart ? this.cartAttributeExpiration(cart) : this.initialCartExpiration
    ]
      .filter((expiration) => expiration > this.currentTimestamp());
    return validExpirations.length ? Math.min(...validExpirations) : 0;
  }

  qualifyingSubtotal(cart) {
    if (!cart) return 0;
    if (typeof cart.items_subtotal_price === 'number') return cart.items_subtotal_price;
    return Number(cart.original_total_price || cart.total_price || 0);
  }

  influencerTierCodeForCart(cart) {
    const subtotal = this.qualifyingSubtotal(cart);
    let code = '';
    this.config.tiers.forEach((tier, index) => {
      const previousTier = this.config.tiers[index - 1];
      const estimatedTotalBeforeUpgrade = previousTier
        ? Math.max(0, subtotal - Number(previousTier.amount || 0))
        : subtotal;

      if (tier.code && estimatedTotalBeforeUpgrade >= Number(tier.threshold || 0)) {
        code = tier.code;
      }
    });
    return code;
  }

  normalQualifyingItemCount(cart) {
    if (!Array.isArray(cart && cart.items)) return 0;
    return cart.items.reduce((total, item) => {
      if (item.properties && item.properties._ark_free_gift) return total;
      return total + Number(item.quantity || 0);
    }, 0);
  }

  normalTierCodeForCart(cart) {
    if (!this.config.normal.enabled) return '';

    const itemCount = this.normalQualifyingItemCount(cart);
    let code = '';
    this.config.normal.tiers.forEach((tier) => {
      if (tier.code && itemCount >= Number(tier.quantity || 0)) code = tier.code;
    });
    return code;
  }

  currentCodes(cart) {
    if (!Array.isArray(cart && cart.discount_codes)) return [];
    return cart.discount_codes
      .map((discount) => String(discount.code || '').trim().toUpperCase())
      .filter(Boolean);
  }

  currentApplicableCodes(cart) {
    if (!Array.isArray(cart && cart.discount_codes)) return [];
    return cart.discount_codes
      .filter((discount) => discount.applicable !== false)
      .map((discount) => String(discount.code || '').trim().toUpperCase())
      .filter(Boolean);
  }

  influencerManagedCodes() {
    return this.config.tiers.map((tier) => tier.code).filter(Boolean);
  }

  normalManagedCodes() {
    return this.config.normal.tiers.map((tier) => tier.code).filter(Boolean);
  }

  managedCodes() {
    return [...this.influencerManagedCodes(), ...this.normalManagedCodes()];
  }

  isManagedCode(code) {
    const normalizedCode = String(code || '').trim().toUpperCase();
    return Boolean(normalizedCode) && this.influencerManagedCodes().includes(normalizedCode);
  }

  isNormalManagedCode(code) {
    const normalizedCode = String(code || '').trim().toUpperCase();
    return Boolean(normalizedCode) && this.normalManagedCodes().includes(normalizedCode);
  }

  async getCart() {
    const response = await fetch(routes.cart_url + '.js', {
      headers: { 'Accept': 'application/json' }
    });
    if (!response.ok) throw new Error('Unable to read the cart');
    return response.json();
  }

  async updateCart(data) {
    const response = await fetch(routes.cart_url + '/update.js', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Unable to update the cart discount');
    return response.json();
  }

  async performSync(cart) {
    let currentCart = cart || await this.getCart();
    if (window.ArkGift) {
      currentCart = await window.ArkGift.sync(currentCart, this);
      if (window.ArkGift.active) return currentCart;
    }

    if (!this.config.enabled) {
      this.removeActivationCookie();
    }

    const expiration = this.config.enabled ? this.activeExpiration(currentCart) : 0;
    const influencerActive = expiration > this.currentTimestamp();

    if (!influencerActive) {
      this.removeActivationCookie();
    } else if (this.cookieExpiration() !== expiration) {
      this.setActivationCookie(expiration);
    }

    const codes = this.currentCodes(currentCart);
    const managedCodes = this.managedCodes();
    const unmanagedCodes = codes.filter((code) => !managedCodes.includes(code));
    const applicableUnmanagedCodes = this.currentApplicableCodes(currentCart)
      .filter((code) => !managedCodes.includes(code));
    const manualPromoActive = !influencerActive && applicableUnmanagedCodes.length > 0;
    const desiredCode = influencerActive
      ? this.influencerTierCodeForCart(currentCart)
      : (manualPromoActive ? '' : this.normalTierCodeForCart(currentCart));
    const desiredCodes = influencerActive
      ? (desiredCode ? [desiredCode] : [])
      : [...unmanagedCodes, ...(desiredCode ? [desiredCode] : [])];
    const attributeIsPresent = this.cartAttributeIsPresent(currentCart);
    const attributeNeedsUpdate = influencerActive
      ? (!this.cartAttributeIsActive(currentCart) || this.cartAttributeExpiration(currentCart) !== expiration)
      : attributeIsPresent;
    const codeNeedsUpdate = codes.length !== desiredCodes.length ||
      desiredCodes.some((code) => !codes.includes(code));

    if (!attributeNeedsUpdate && !codeNeedsUpdate) return currentCart;

    const data = {};
    if (influencerActive && attributeNeedsUpdate) {
      data.attributes = {
        [this.config.attributeKey]: this.config.attributeValue,
        [this.config.expirationAttributeKey]: String(expiration)
      };
    } else if (!influencerActive && attributeNeedsUpdate) {
      data.attributes = {
        [this.config.attributeKey]: null,
        [this.config.expirationAttributeKey]: null
      };
    }
    if (codeNeedsUpdate) data.discount = desiredCodes.join(',');

    const updatedCart = await this.updateCart(data);
    window.dispatchEvent(new CustomEvent('influencer-discount:updated', {
      detail: {
        cart: updatedCart,
        code: desiredCode,
        mode: influencerActive ? 'influencer' : (manualPromoActive ? 'promo' : 'normal')
      }
    }));
    return updatedCart;
  }

  sync(cart, force = false) {
    if (this.initialized && !cart && !force && !this.isActive() && !window.ArkGift?.needsSync()) return Promise.resolve(null);

    const shouldReadLatestCart = this.pendingSyncs > 0;
    this.pendingSyncs += 1;
    const queuedSync = this.syncQueue
      .catch(() => null)
      .then(() => this.performSync(shouldReadLatestCart ? null : cart));
    this.syncQueue = queuedSync;
    return queuedSync.finally(() => {
      this.pendingSyncs -= 1;
    });
  }

  async activateInfluencerDiscount() {
    if (!this.config.enabled) return null;

    if (window.ArkGift) window.ArkGift.entry = false;
    const expiration = this.activeExpiration() || this.createExpirationTimestamp();
    if (this.cookieExpiration() !== expiration) {
      this.setActivationCookie(expiration);
    }

    try {
      const updatedCart = await this.sync(null, true);
      await this.refreshCartUi();
      return updatedCart;
    } catch (error) {
      console.error('Influencer discount activation failed:', error);
      return null;
    }
  }

  isActive(cart) {
    return this.config.enabled && this.activeExpiration(cart) > this.currentTimestamp();
  }

  clearActivation() {
    this.initialCartExpiration = 0;
    this.initialCartAttributeActive = false;
    this.initialCartAttributePresent = false;
    this.initialManagedCodePresent = false;
    this.removeActivationCookie();
    sessionStorage.removeItem('arkGiftPopup');
  }

  async deactivate() {
    await this.syncQueue.catch(() => null);
    const cart = await this.getCart();
    await this.updateCart({
      discount: this.currentCodes(cart).filter((code) => !this.isManagedCode(code)).join(','),
      attributes: { _discount_track: '', _discount_track_expires: '' }
    });
    this.clearActivation();
    const url = new URL(location.href);
    if (url.searchParams.get('ark_flow') === 'influencer') url.searchParams.delete('ark_flow');
    location.replace(url.toString());
  }

  refreshCartUi() {
    if (window.CartPage && document.getElementById('cart')) {
      return window.CartPage.updateCart();
    }
    if (window.SideCart) {
      return window.SideCart.updateSideCart(false);
    }
    return Promise.resolve();
  }

  async init() {
    const activatedFromUrl = this.activationInUrl();
    if (this.config.enabled && activatedFromUrl) {
      if (window.ArkGift) window.ArkGift.entry = false;
      const expiration = this.activeExpiration() || this.createExpirationTimestamp();
      if (this.cookieExpiration() !== expiration) {
        this.setActivationCookie(expiration);
      }
    }
    const shouldReadCart = window.ArkGift?.needsSync() || activatedFromUrl ||
      this.cookieIsActive() ||
      this.initialCartAttributeActive ||
      this.initialCartAttributePresent ||
      this.initialManagedCodePresent ||
      this.initialNormalManagedCodePresent ||
      (this.config.normal.enabled && this.initialCartItemCount > 0);
    if (!shouldReadCart) {
      this.initialized = true;
      return;
    }

    try {
      const cart = await this.getCart();
      const updatedCart = await this.sync(cart);
      if (updatedCart !== cart) await this.refreshCartUi();
    } catch (error) {
      console.error('Influencer discount initialization failed:', error);
    } finally {
      this.initialCartAttributeActive = false;
      this.initialCartAttributePresent = false;
      this.initialCartExpiration = 0;
      this.initialManagedCodePresent = false;
      this.initialNormalManagedCodePresent = false;
      this.initialCartItemCount = 0;
      this.initialized = true;
    }
  }
}

window.InfluencerDiscount = new ArkInfluencerDiscountController();
